import {
  useDocumentVisibility,
  useIntersection,
  useMediaQuery,
} from '@mantine/hooks'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { Link } from 'react-router'
import TablerChevronLeft from '~icons/tabler/chevron-left'
import TablerChevronRight from '~icons/tabler/chevron-right'
import Logo from 'src/components/Logo'
import { cn } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { isExternalUrl } from 'src/utils/url'
import styles from './BannerCarousel.module.css'
import { useBannerCarousel } from './useBannerCarousel'

export interface BannerSlide {
  id: string
  imageUrl: string
  mobileImageUrl?: string
  targetUrl: string
  alt?: string
  blank?: boolean
  overlay?: React.ReactNode
  imageClassName?: string
}

export interface BannerCarouselProps {
  slides: BannerSlide[]
  className?: string
  style?: CSSProperties
  autoplayInterval?: number
  firstSlideInterval?: number
  isMobile?: boolean
  /** 'full' (default): single slide fills width. 'peek': center slide with neighbors visible. */
  variant?: 'full' | 'peek'
}

const TRANSITION_DURATION = 400
const EXPOSE_THRESHOLD = 1000
const FAILOVER_SKIP_DELAY = 3000
const MOUSE_IDLE_TIMEOUT = 3000

// Desktop banners are designed at ~5.6:1; mobile banners are designed
// taller so the 5.6:1 placeholder collapses to a sliver on narrow screens.
// Keep these in sync with the dynamic-config image dimensions.
export const BANNER_ASPECT_RATIO_DESKTOP = '5.6 / 1'
export const BANNER_ASPECT_RATIO_MOBILE = '2 / 1'

const SESSION_KEYS = {
  expose: 'banner_expose_tracked',
  cycle: 'banner_cycle_tracked',
} as const

type SessionKey = (typeof SESSION_KEYS)[keyof typeof SESSION_KEYS]

function getSessionSet(key: SessionKey): Set<string> {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
  } catch {
    return new Set()
  }
}

function addToSessionSet(key: SessionKey, value: string): void {
  try {
    const raw = sessionStorage.getItem(key)
    const arr = raw ? (JSON.parse(raw) as string[]) : []
    if (!arr.includes(value)) arr.push(value)
    sessionStorage.setItem(key, JSON.stringify(arr))
  } catch {
    // sessionStorage unavailable
  }
}

function useMouseActive() {
  const [active, setActive] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      setActive(true)
      timerRef.current = setTimeout(() => setActive(false), MOUSE_IDLE_TIMEOUT)
    }

    document.addEventListener('mousemove', reset)
    return () => {
      document.removeEventListener('mousemove', reset)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return active
}

export function BannerCarousel({
  slides,
  className,
  style,
  autoplayInterval = 5000,
  firstSlideInterval = 7000,
  isMobile = false,
  variant = 'full',
}: BannerCarouselProps) {
  const total = slides.length

  if (total === 0) return null

  if (total === 1) {
    return (
      <SingleBanner
        slide={slides[0]}
        className={className}
        style={style}
        isMobile={isMobile}
      />
    )
  }

  return (
    <CarouselInner
      slides={slides}
      className={className}
      style={style}
      autoplayInterval={autoplayInterval}
      firstSlideInterval={firstSlideInterval}
      isMobile={isMobile}
      variant={variant}
    />
  )
}

function SingleBanner({
  slide,
  className,
  style,
  isMobile,
}: {
  slide: BannerSlide
  className?: string
  style?: CSSProperties
  isMobile: boolean
}) {
  const displayStartRef = useRef(Date.now())
  const getDisplayDuration = useCallback(
    () => Math.max(0, Date.now() - displayStartRef.current),
    [],
  )
  const { ref: intersectionRef, entry: intersection } = useIntersection({
    threshold: 0.1,
  })
  const isInView = Boolean(intersection?.isIntersecting)
  const sourceRef = useRef<'auto' | 'manual'>('auto')

  useExposeTracking([slide], 0, 1, sourceRef, isInView)

  return (
    <div
      ref={intersectionRef}
      className={cn(
        'relative overflow-hidden',
        isMobile ? 'rounded-xl' : 'rounded-2xl',
        className,
      )}
      style={style}
    >
      <BannerLink
        slide={slide}
        position={0}
        totalCount={1}
        isActive
        getDisplayDuration={getDisplayDuration}
      >
        <BannerImage slide={slide} isMobile={isMobile} eager />
      </BannerLink>
    </div>
  )
}

function CarouselInner({
  slides,
  className,
  style,
  autoplayInterval,
  firstSlideInterval,
  isMobile,
  variant = 'full',
}: Required<
  Pick<BannerCarouselProps, 'autoplayInterval' | 'firstSlideInterval'>
> &
  Pick<BannerCarouselProps, 'className' | 'style' | 'variant'> & {
    slides: BannerSlide[]
    isMobile: boolean
  }) {
  const isPeek = variant === 'peek'
  const total = slides.length
  const prefersReducedMotion =
    useMediaQuery('(prefers-reduced-motion: reduce)') ?? false
  const mouseActive = useMouseActive()

  const handleSwipe = useCallback(
    (info: {
      direction: 'left' | 'right'
      fromIndex: number
      toIndex: number
    }) => {
      track('banner_swipe', {
        direction: info.direction,
        from_id: slides[info.fromIndex]?.id,
        to_id: slides[info.toIndex]?.id,
      })
    },
    [slides],
  )

  const handleAutoplayCycle = useCallback(
    (info: { totalBanners: number; cycleCount: number }) => {
      const cycleKey = `cycle_${info.totalBanners}`
      if (getSessionSet(SESSION_KEYS.cycle).has(cycleKey)) return
      addToSessionSet(SESSION_KEYS.cycle, cycleKey)
      track('banner_autoplay_cycle', {
        total_banners: info.totalBanners,
        cycle_count: info.cycleCount,
      })
    },
    [],
  )

  const hoveringRef = useRef(false)
  const [hovering, setHovering] = useState(false)

  const carousel = useBannerCarousel({
    total,
    autoplayInterval,
    firstSlideInterval,
    autoplay: !prefersReducedMotion,
    reducedMotion: prefersReducedMotion,
    transitionDuration: TRANSITION_DURATION,
    hoveringRef,
    onSwipe: handleSwipe,
    onAutoplayCycle: handleAutoplayCycle,
  })

  const {
    currentIndex,
    renderIndex,
    isTransitioning,
    goTo,
    next,
    prev,
    pause,
    resume,
    trackRef,
    dragHandlers,
    dragOffset,
    isDragging,
    notifyManualInteraction,
    lastAdvanceSourceRef,
    activeInterval,
    isAutoplayActive,
  } = carousel

  const renderSlides = useMemo(() => {
    return [slides.at(-1)!, ...slides, slides[0]]
  }, [slides, total])

  const totalWithClones = total + 2

  const enableTransition = isTransitioning && !isDragging

  const slidePercent = 100 / totalWithClones
  const visibleIndex = prefersReducedMotion ? currentIndex + 1 : renderIndex
  const trackStyle: CSSProperties = {
    display: 'flex',
    width: `${totalWithClones * 100}%`,
    transform: `translateX(calc(-${visibleIndex * slidePercent}% + ${dragOffset}px))`,
    transition: enableTransition
      ? `transform ${TRANSITION_DURATION}ms ${prefersReducedMotion ? 'ease' : 'cubic-bezier(0.25, 0.1, 0.25, 1)'}`
      : 'none',
    willChange: isTransitioning || isDragging ? 'transform' : undefined,
  }

  const handleMouseEnter = useCallback(() => {
    hoveringRef.current = true
    setHovering(true)
    pause()
  }, [pause])

  const handleMouseLeave = useCallback(() => {
    hoveringRef.current = false
    setHovering(false)
    resume()
  }, [resume])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        notifyManualInteraction()
        prev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        notifyManualInteraction()
        next()
      }
    },
    [prev, next, notifyManualInteraction],
  )

  const handlePrev = useCallback(() => {
    const fromId = slides[currentIndex]?.id
    const toIdx = (currentIndex - 1 + total) % total
    const toId = slides[toIdx]?.id
    track('banner_arrow_click', {
      direction: 'left',
      from_id: fromId,
      to_id: toId,
    })
    notifyManualInteraction()
    prev()
  }, [prev, currentIndex, total, slides, notifyManualInteraction])

  const handleNext = useCallback(() => {
    const fromId = slides[currentIndex]?.id
    const toIdx = (currentIndex + 1) % total
    const toId = slides[toIdx]?.id
    track('banner_arrow_click', {
      direction: 'right',
      from_id: fromId,
      to_id: toId,
    })
    notifyManualInteraction()
    next()
  }, [next, currentIndex, total, slides, notifyManualInteraction])

  const handleAutoSkip = useCallback(() => {
    lastAdvanceSourceRef.current = 'auto'
    next()
    resume()
  }, [next, resume, lastAdvanceSourceRef])

  const handleDotClick = useCallback(
    (index: number) => {
      track('banner_dot_click', {
        from_position: currentIndex,
        to_position: index,
      })
      notifyManualInteraction()
      goTo(index)
    },
    [goTo, currentIndex, notifyManualInteraction],
  )

  const { ref: intersectionRef, entry: intersection } = useIntersection({
    threshold: 0.1,
  })
  const isInView = Boolean(intersection?.isIntersecting)

  const displayStartRef = useRef(Date.now())
  useEffect(() => {
    displayStartRef.current = Date.now()
  }, [currentIndex])

  const getDisplayDuration = useCallback(
    () => Math.max(0, Date.now() - displayStartRef.current),
    [],
  )

  useExposeTracking(slides, currentIndex, total, lastAdvanceSourceRef, isInView)

  const [fadeFromIndex, setFadeFromIndex] = useState<number | null>(null)
  const lastIndexRef = useRef(currentIndex)

  useEffect(() => {
    if (!prefersReducedMotion) {
      lastIndexRef.current = currentIndex
      setFadeFromIndex(null)
      return
    }

    if (lastIndexRef.current !== currentIndex) {
      const previousIndex = lastIndexRef.current
      lastIndexRef.current = currentIndex
      setFadeFromIndex(previousIndex)

      const timer = window.setTimeout(() => {
        setFadeFromIndex(null)
      }, TRANSITION_DURATION)

      return () => {
        window.clearTimeout(timer)
      }
    }
  }, [currentIndex, prefersReducedMotion])

  useEffect(() => {
    if (!prefersReducedMotion || total <= 1) return
    const resolveSrc = (slide: BannerSlide | undefined) => {
      if (!slide) return undefined
      return isMobile && slide.mobileImageUrl
        ? slide.mobileImageUrl
        : slide.imageUrl
    }

    const prevIndex = (currentIndex - 1 + total) % total
    const nextIndex = (currentIndex + 1) % total
    const prevSrc = resolveSrc(slides[prevIndex])
    const nextSrc = resolveSrc(slides[nextIndex])

    if (prevSrc) {
      const image = new Image()
      image.src = prevSrc
    }
    if (nextSrc) {
      const image = new Image()
      image.src = nextSrc
    }
  }, [prefersReducedMotion, currentIndex, slides, total, isMobile])

  if (prefersReducedMotion) {
    const activeSlide = slides[currentIndex]
    if (!activeSlide) return null

    const fadeIndices =
      fadeFromIndex != null && fadeFromIndex !== currentIndex
        ? [fadeFromIndex, currentIndex]
        : [currentIndex]

    return (
      <section
        ref={intersectionRef}
        className={cn('relative group', className)}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        aria-roledescription="carousel"
        aria-label="Banner carousel"
        tabIndex={0}
      >
        <div
          className={cn(
            'overflow-hidden',
            isMobile ? 'rounded-xl' : 'rounded-2xl',
            isPeek && 'lg:px-[20%]',
          )}
        >
          <div
            ref={trackRef}
            className={cn(
              'relative w-full',
              isDragging && 'cursor-grabbing',
              'touch-pan-y',
            )}
            {...dragHandlers}
          >
            {fadeIndices.map(index => {
              const slide = slides[index]
              const isActive = index === currentIndex
              const isNeighbor =
                index === (currentIndex - 1 + total) % total ||
                index === (currentIndex + 1) % total
              if (!slide) return null

              return (
                <div
                  key={`${slide.id}-${index}`}
                  className={cn(
                    'absolute inset-0 transition-opacity',
                    isActive ? 'opacity-100' : 'opacity-0',
                  )}
                  style={{ transitionDuration: `${TRANSITION_DURATION}ms` }}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${total}`}
                  aria-hidden={!isActive}
                >
                  <BannerLink
                    slide={slide}
                    position={index}
                    totalCount={total}
                    preventClick={isDragging}
                    isActive={isActive}
                    getDisplayDuration={getDisplayDuration}
                  >
                    <BannerImage
                      slide={slide}
                      isMobile={isMobile}
                      eager={isActive}
                      preload={isNeighbor}
                      onRequestSkip={isActive ? handleAutoSkip : undefined}
                    />
                  </BannerLink>
                </div>
              )
            })}
          </div>
        </div>

        {!isMobile && (
          <NavArrows
            visible={mouseActive || hovering}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}

        <Indicators
          total={total}
          currentIndex={currentIndex}
          onDotClick={handleDotClick}
          isMobile={isMobile}
          progressDuration={activeInterval}
          isPaused={hovering || prefersReducedMotion || isDragging}
          isAutoplayActive={isAutoplayActive}
        />
      </section>
    )
  }

  return (
    <section
      ref={intersectionRef}
      className={cn('relative group', className)}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      aria-roledescription="carousel"
      aria-label="Banner carousel"
      tabIndex={0}
    >
      <div
        className={cn(
          'overflow-hidden',
          isMobile ? 'rounded-xl' : 'rounded-2xl',
          isPeek && 'lg:px-[20%]',
        )}
      >
        <div
          ref={trackRef}
          style={trackStyle}
          className={cn(isDragging && 'cursor-grabbing', 'touch-pan-y')}
          {...dragHandlers}
        >
          {renderSlides.map((slide, i) => {
            const isVisible = i === visibleIndex
            const isNeighbor = i === visibleIndex - 1 || i === visibleIndex + 1
            const normalizedPosition = (i - 1 + total) % total
            return (
              <div
                key={`${slide.id}-${i}`}
                className={cn('flex-shrink-0', isPeek && 'lg:px-2')}
                style={{ width: `${slidePercent}%` }}
                role="group"
                aria-roledescription="slide"
                aria-label={`${((i - 1 + total) % total) + 1} of ${total}`}
                aria-hidden={!isVisible}
              >
                <BannerLink
                  slide={slide}
                  position={normalizedPosition}
                  totalCount={total}
                  preventClick={isDragging}
                  isActive={isVisible}
                  getDisplayDuration={getDisplayDuration}
                >
                  <BannerImage
                    slide={slide}
                    isMobile={isMobile}
                    eager={i === 1}
                    preload={isNeighbor}
                    onRequestSkip={
                      i === visibleIndex ? handleAutoSkip : undefined
                    }
                  />
                </BannerLink>
              </div>
            )
          })}
        </div>
      </div>

      {!isMobile && (
        <NavArrows
          visible={mouseActive || hovering}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}

      <Indicators
        total={total}
        currentIndex={currentIndex}
        onDotClick={handleDotClick}
        isMobile={isMobile}
        progressDuration={activeInterval}
        isPaused={hovering || prefersReducedMotion || isDragging}
        isAutoplayActive={isAutoplayActive}
      />
    </section>
  )
}

function NavArrows({
  visible,
  onPrev,
  onNext,
}: {
  visible: boolean
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <>
      <button
        type="button"
        className={cn(
          'absolute left-2 top-1/2 -translate-y-1/2 z-10',
          'flex items-center justify-center w-8 h-8',
          'rounded-full text-white backdrop-blur-sm',
          'bg-black/20 hover:bg-black/50',
          'transition-[opacity,background-color] duration-300',
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
        )}
        onClick={onPrev}
        aria-label="Previous slide"
        tabIndex={visible ? 0 : -1}
      >
        <TablerChevronLeft className="size-5" />
      </button>
      <button
        type="button"
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 z-10',
          'flex items-center justify-center w-8 h-8',
          'rounded-full text-white backdrop-blur-sm',
          'bg-black/20 hover:bg-black/50',
          'transition-[opacity,background-color] duration-300',
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
        )}
        onClick={onNext}
        aria-label="Next slide"
        tabIndex={visible ? 0 : -1}
      >
        <TablerChevronRight className="size-5" />
      </button>
    </>
  )
}

function Indicators({
  total,
  currentIndex,
  onDotClick,
  isMobile,
  progressDuration,
  isPaused,
  isAutoplayActive,
}: {
  total: number
  currentIndex: number
  onDotClick: (index: number) => void
  isMobile: boolean
  progressDuration?: number
  isPaused?: boolean
  isAutoplayActive?: boolean
}) {
  return (
    <nav
      className={cn(
        'flex items-center justify-center',
        'absolute left-1/2 -translate-x-1/2',
        isMobile
          ? 'bottom-1.5 gap-1'
          : 'bottom-3 gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1.5',
      )}
      style={
        isMobile
          ? { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }
          : undefined
      }
      aria-label="Slide indicators"
    >
      {Array.from({ length: total }, (_, i) => {
        const isActive = i === currentIndex
        return (
          <button
            key={i}
            type="button"
            aria-current={isActive ? 'step' : undefined}
            aria-label={`Go to slide ${i + 1}`}
            className={cn(
              'relative rounded-full transition-all duration-300 border-none cursor-pointer overflow-hidden',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
              isMobile
                ? isActive
                  ? cn(
                      'w-4 h-1.5',
                      isAutoplayActive && !isPaused
                        ? 'bg-white/50'
                        : 'bg-white',
                    )
                  : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/90'
                : isActive
                  ? cn(
                      'w-5 h-2',
                      isAutoplayActive && !isPaused
                        ? 'bg-white/40'
                        : 'bg-white',
                    )
                  : 'w-2 h-2 bg-white/50 hover:bg-white/80',
            )}
            onClick={() => onDotClick(i)}
          >
            {isActive && progressDuration && isAutoplayActive && (
              <span
                key={currentIndex}
                className={cn(
                  'absolute inset-0 rounded-full origin-left bg-white',
                  styles.indicatorFill,
                )}
                style={{
                  animationDuration: `${progressDuration}ms`,
                  animationTimingFunction: 'linear',
                  animationFillMode: 'forwards',
                  animationPlayState: isPaused ? 'paused' : 'running',
                  willChange: 'transform',
                }}
              />
            )}
          </button>
        )
      })}
    </nav>
  )
}

function BannerLink({
  slide,
  position,
  totalCount,
  children,
  preventClick,
  isActive,
  getDisplayDuration,
}: {
  slide: BannerSlide
  position: number
  totalCount: number
  children: React.ReactNode
  preventClick?: boolean
  isActive?: boolean
  getDisplayDuration?: () => number
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (preventClick) {
        e.preventDefault()
        return
      }
      const displayDuration = isActive ? getDisplayDuration?.() : undefined
      track('banner_click', {
        banner_id: slide.id,
        position,
        link_url: slide.targetUrl,
        total_count: totalCount,
        display_duration: displayDuration,
      })
    },
    [slide, position, totalCount, preventClick, isActive, getDisplayDuration],
  )

  const opensInNewTab = slide.blank ?? isExternalUrl(slide.targetUrl)

  return (
    <Link
      to={slide.targetUrl}
      className="relative block select-none"
      draggable={false}
      onClick={handleClick}
      target={opensInNewTab ? '_blank' : undefined}
      rel={opensInNewTab ? 'noopener noreferrer' : undefined}
    >
      {children}
      {slide.overlay}
    </Link>
  )
}

function BannerImage({
  slide,
  isMobile,
  eager,
  preload,
  onRequestSkip,
}: {
  slide: BannerSlide
  isMobile: boolean
  eager?: boolean
  preload?: boolean
  onRequestSkip?: () => void
}) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  )
  const usesMobileSrc = isMobile && Boolean(slide.mobileImageUrl)
  const src = usesMobileSrc ? slide.mobileImageUrl! : slide.imageUrl
  const skipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (preload && src) {
      const image = new Image()
      image.src = src
    }
  }, [preload, src])

  useEffect(() => {
    setStatus('loading')
  }, [src])

  useEffect(() => {
    if (status === 'error' && onRequestSkip) {
      if (skipTimerRef.current) {
        clearTimeout(skipTimerRef.current)
      }
      skipTimerRef.current = setTimeout(() => {
        onRequestSkip()
      }, FAILOVER_SKIP_DELAY)
    }

    return () => {
      if (skipTimerRef.current) {
        clearTimeout(skipTimerRef.current)
        skipTimerRef.current = null
      }
    }
  }, [status, onRequestSkip])

  // Match the placeholder ratio to the src that will actually load:
  // when mobileImageUrl is missing we fall back to the desktop image,
  // so a 2:1 placeholder followed by a 5.6:1 image would itself reflow.
  const hasCustomImage = !!slide.imageClassName
  const placeholderAspectRatio = usesMobileSrc
    ? BANNER_ASPECT_RATIO_MOBILE
    : BANNER_ASPECT_RATIO_DESKTOP
  const wrapperStyle =
    !hasCustomImage && (status === 'loading' || status === 'error')
      ? { aspectRatio: placeholderAspectRatio }
      : undefined

  return (
    <div className="relative w-full" style={wrapperStyle}>
      {status === 'loading' && (
        <div
          className={cn(
            'absolute inset-0 bg-zinc-800/50 animate-pulse',
            isMobile ? 'rounded-xl' : 'rounded-2xl',
          )}
        />
      )}

      {status === 'error' && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900',
            isMobile ? 'rounded-xl' : 'rounded-2xl',
          )}
        >
          <div className="flex flex-col items-center gap-2 text-zinc-400">
            <Logo className="size-6" />
            <span className="text-xs">Failed to load banner</span>
          </div>
        </div>
      )}

      {status !== 'error' && (
        <img
          src={src}
          alt={slide.alt || 'PixAI promotion banner'}
          className={cn(
            'w-full block',
            status === 'loading' && 'opacity-0',
            slide.imageClassName,
          )}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
          draggable={false}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      )}
    </div>
  )
}

function useExposeTracking(
  slides: BannerSlide[],
  currentIndex: number,
  total: number,
  sourceRef: React.RefObject<'auto' | 'manual'>,
  isInView: boolean,
) {
  const exposeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const documentVisibility = useDocumentVisibility()

  useEffect(() => {
    if (exposeTimerRef.current) {
      clearTimeout(exposeTimerRef.current)
    }

    const slide = slides[currentIndex]
    if (!slide || !isInView || documentVisibility !== 'visible') return

    if (getSessionSet(SESSION_KEYS.expose).has(slide.id)) return

    // Mark immediately to prevent duplicate timers for the same slide
    addToSessionSet(SESSION_KEYS.expose, slide.id)

    exposeTimerRef.current = setTimeout(() => {
      track('banner_expose', {
        banner_id: slide.id,
        position: currentIndex,
        total_count: total,
        source: sourceRef.current,
      })
    }, EXPOSE_THRESHOLD)

    return () => {
      if (exposeTimerRef.current) {
        clearTimeout(exposeTimerRef.current)
      }
    }
  }, [currentIndex, slides, total, isInView, documentVisibility])
}
